// apps/client/src/ui/screens/museum.tsx (M2-T10) — Professor Reed's counter and the collection (layer 'panel').
// mode 'curator': greeting, Identify fossils (n), Donate, See the collection, Bye. mode 'hall': the fossil hall and aquarium as two
// tabs with progress and "Donated by <name>" (a former resident's name comes from the server). mode 'plaque': one display's set.
import { getEntry, itemsOfKind, t as tt, type CatalogId, type StringKey, type StringParams } from '@whiskerton/content';
import type { MuseumEntryView } from '@whiskerton/shared';
import { fossilSets } from '@whiskerton/shared/rules/museum';
import { useEffect, useState, type ReactNode } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useGame } from '../../state/store';
import { itemName, useT } from '../i18n';
import { useIcon } from '../items/useIcon';
import { Bubble, Button, Panel, Tabs } from '../kit';
import type { ScreenDef } from '../router';
import { injectShopStyle } from './sell';

export type MuseumParams = { mode: 'curator' } | { mode: 'hall' } | { mode: 'plaque'; set: string };
declare module '../router' { interface ScreenParams { museum: MuseumParams } }

// nameOf, inlineName and setTitle are shared with donate.tsx (same feature, M2-T10); nothing outside the museum screens uses them.
/** A catalog item's display name ("Triceratops skull"), or its id when the catalog has no such item. */
export const nameOf = (id: CatalogId): string => { const e = getEntry(id); return e && e.kind !== 'world' ? itemName(e.name) : id; };
/** The name as it reads mid-sentence in the curator's lines (fish names are lowercase there). */
export const inlineName = (id: CatalogId): string => { const e = getEntry(id); return e && e.kind !== 'world' ? tt(e.name as StringKey) : id; };
/** A display's title: the set name for a multi-part fossil (`fossils.set<Set>`), else the single part's name. */
export function setTitle(set: { set: string; parts: readonly CatalogId[] }): string {
  if (set.parts.length === 1) return nameOf(set.parts[0]!);
  return itemName(`fossils.set${set.set.charAt(0).toUpperCase()}${set.set.slice(1)}`);
}
/** The player's own name, for the curator's lines. */
const usePlayer = () => useGame((s) => (s.you ? (s.residents[s.you.resident]?.name ?? '') : ''));

type Line = { key: StringKey; params?: StringParams };

function Curator({ close, setMode }: { close: () => void; setMode(m: MuseumParams): void }) {
  const t = useT();
  const player = usePlayer();
  const { unidentified, identifyAll, openScreen } = useGame(useShallow((s) => ({ unidentified: s.museumUnidentified().length, identifyAll: s.museumIdentifyAll,
    openScreen: s.openScreen, items: s.you?.items })));                  // re-render when your pockets change
  const [line, setLine] = useState<Line | null>(null);                  // null: the greeting, named at render time (residents may arrive late)
  const [busy, setBusy] = useState(false);
  const identify = () => {
    setBusy(true);
    void identifyAll().then((items) => {
      if (items === null) return;                                         // rejected: the action client already showed why
      if (items.length === 0) setLine({ key: 'curator.identifyNone' });
      else if (items.length === 1) setLine({ key: 'curator.identified', params: { fossil: inlineName(items[0]!.catalogId) } });
      else setLine({ key: 'curator.identifiedMany', params: { n: items.length } });
    }).finally(() => setBusy(false));
  };
  return (
    <>
      <Bubble name={t('curator.name')}>{line ? t(line.key, line.params) : t('curator.greeting', { player })}</Bubble>
      <div className="museum-actions">
        <Button disabled={busy || unidentified === 0} onClick={identify}>{t('museum.menuIdentify', { n: unidentified })}</Button>
        <Button onClick={() => openScreen('donate', undefined)}>{t('museum.menuDonate')}</Button>
        <Button variant="quiet" onClick={() => setMode({ mode: 'hall' })}>{t('museum.menuHall')}</Button>
        <Button variant="quiet" onClick={close}>{t('museum.bye')}</Button>
      </div>
    </>
  );
}

function EntryRow({ id, entry }: { id: CatalogId; entry: MuseumEntryView | undefined }) {
  const t = useT();
  const icon = useIcon(entry ? id : null);
  return (
    <div className={`shop-row museum-row${entry ? '' : ' museum-missing'}`}>
      <img src={icon} alt="" />
      <span className="museum-text">
        <span className="shop-name">{nameOf(id)}</span>
        <span className="shop-price">{entry ? t('museum.donatedBy', { name: entry.donor.name }) : t('museum.notYet')}</span>
      </span>
    </div>
  );
}

function Hall({ bye }: { bye: ReactNode }) {
  const t = useT();
  const state = useGame((s) => s.museumState);
  const [tab, setTab] = useState<'fossils' | 'fish'>('fossils');
  const fossils = fossilSets().flatMap((s) => s.parts), fish = itemsOfKind('fish').map((f) => f.id);
  const by = new Map([...(state?.fossils ?? []), ...(state?.fish ?? [])].map((e) => [e.catalogId, e]));
  const ids = tab === 'fossils' ? fossils : fish;
  const count = (list: readonly CatalogId[]) => list.filter((id) => by.has(id)).length;
  const label = (key: StringKey, list: readonly CatalogId[]) => `${t(key)} · ${t('museum.progress', { n: count(list), total: list.length })}`;
  return (
    <>
      <div className="museum-head">
        <Tabs tabs={[{ id: 'fossils', label: label('museum.hallFossils', fossils) }, { id: 'fish', label: label('museum.hallFish', fish) }]}
          active={tab} onChange={(id) => setTab(id === 'fish' ? 'fish' : 'fossils')} />
        {bye}
      </div>
      <div className="shop-list museum-list museum-grid">{ids.map((id) => <EntryRow key={id} id={id} entry={by.get(id)} />)}</div>
    </>
  );
}

function Plaque({ set, bye }: { set: string; bye: ReactNode }) {
  const t = useT();
  const state = useGame((s) => s.museumState);
  const def = fossilSets().find((s) => s.set === set);
  if (!def) return <><p className="shop-note">{t('museum.notYet')}</p><div className="museum-actions">{bye}</div></>;
  const by = new Map((state?.fossils ?? []).map((e) => [e.catalogId, e]));
  return (
    <>
      <div className="museum-head"><h4 className="museum-plaque-title">{setTitle(def)}</h4>{bye}</div>
      <div className="shop-list museum-list">
        {def.parts.map((id) => {
          const e = by.get(id);
          return (
            <div key={id} className={`shop-row museum-row museum-plaque${e ? '' : ' museum-missing'}`}>
              <span className="shop-name">{nameOf(id)}</span>
              <span className="shop-price">{e ? t('museum.donatedBy', { name: e.donor.name }) : t('museum.notYet')}</span>
            </div>
          );
        })}
      </div>
    </>
  );
}

const CSS = `
.museum-actions { display: flex; flex-wrap: wrap; gap: 10px; }
.museum-list { max-height: 52cqh; overflow-y: auto; padding: 2px; }
/* M2 playtest (shot 39): the panel body scrolled as well as the list, carrying the hall tabs up under the title tab. The panel is a
   column that never scrolls itself; only the list does, in whatever height is left. */
.panel.museum-panel { display: flex; flex-direction: column; overflow: hidden; }
.museum-panel > * { flex: none; }
.museum-panel > .museum-list { flex: 0 1 auto; min-height: 0; max-height: none; }
/* M2 playtest (d5): at 844 × 390 the list window held one 60 px row. Bye sits on the tab row, and the entries are compact cards (name
   over credit) in as many columns as fit, so a phone in landscape shows several at a glance. */
.museum-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.museum-head .tabs { flex-wrap: nowrap; min-width: 0; }
.museum-head .tab { white-space: nowrap; }
.museum-head .btn { flex: none; }
.museum-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 6px; align-content: start; }
.museum-row { grid-template-columns: 34px 1fr; gap: 10px; padding: 5px 10px; }
.museum-row img { width: 34px; height: 34px; }
.museum-text { display: grid; min-width: 0; line-height: 1.2; }
.museum-text > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.museum-text .shop-price { font-size: 13px; text-align: left; }
.museum-plaque { grid-template-columns: 1fr auto; padding: 10px 14px; }
.museum-missing { opacity: .55; }
.museum-plaque-title { margin: 0; font-weight: 900; font-size: 18px; color: var(--wood-700); }
/* v1 round 3 (844 × 390: the in-town panel is 262 px tall and the menu was 286, so Bye ran past it; 750 × 340: the second row of
   buttons was a sliver). Professor Reed's line runs the panel's width, and his four choices are compact pills on one row. */
.museum-panel .bubble { max-width: none; }
.museum-actions .btn { font-size: 15px; padding: 10px 16px 11px; white-space: nowrap; }
/* The donate picker: his thanks beside Bye on one row, then the slots, which scroll on their own in whatever height is left (750 × 340:
   five 96 px slots a row were cut off below the panel). */
.donate-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.donate-head .bubble { flex: 1 1 auto; min-width: 0; }
.donate-head .btn { flex: none; margin-left: auto; }
.museum-panel > .donate-slots { flex: 0 1 auto; min-height: 0; overflow-y: auto; padding: 2px; display: grid;
  grid-template-columns: repeat(auto-fill, minmax(58px, 1fr)); gap: 8px; align-content: start; }
`;
let styled = false;
export function injectMuseumStyle(): void {
  injectShopStyle();
  if (styled || typeof document === 'undefined') return;
  styled = true;
  const el = document.createElement('style');
  el.dataset.owner = 'museum';
  el.textContent = CSS;
  document.head.appendChild(el);
}

function Museum({ params, close }: { params: MuseumParams; close: () => void }) {
  injectMuseumStyle();
  const t = useT();
  const space = useGame((s) => s.space);
  const [mode, setMode] = useState<MuseumParams>(params);
  useEffect(() => { setMode(params); }, [params]);
  useEffect(() => { if (space !== 'museum') close(); }, [space, close]);   // left the museum (or the town) under the panel
  const bye = <Button variant="quiet" onClick={close}>{t('museum.bye')}</Button>;
  return (
    <Panel title={mode.mode === 'curator' ? t('curator.name') : t('museum.title')} className="shop-panel museum-panel">
      {mode.mode === 'curator' && <Curator close={close} setMode={setMode} />}
      {mode.mode === 'hall' && <Hall bye={bye} />}
      {mode.mode === 'plaque' && <Plaque set={mode.set} bye={bye} />}
    </Panel>
  );
}
export const screen: ScreenDef<'museum'> = { id: 'museum', layer: 'panel', component: ({ params, close }) => <Museum params={params} close={close} /> };
